/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ChangeEvent, type KeyboardEvent, type MouseEvent } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Checkbox } from "../Checkbox";
import { useTableContext } from "./context";
import { TableCell } from "./TableCell";

type Props<Item> = {
  item: Item;
  className?: string;
};

export function TableRow<Item>({ item, className }: Props<Item>) {
  const { t } = useTranslation();
  const {
    columns,
    onRenderCell,
    rowDensity,
    onRowClick,
    selection,
    emptyState,
    gridTemplateColumns,
  } = useTableContext<Item>();

  const heightClass = {
    dense: "h-[42px]",
    default: "h-[52px]",
    expanded: "h-[60px]",
  }[rowDensity.value];

  const handleClick = () => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleCheckboxCellClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (e.target === e.currentTarget && selection) {
      const fakeEvent = {
        nativeEvent: e,
      } as unknown as ChangeEvent<HTMLInputElement>;
      selection.onSelectRow(item)(!isSelected, fakeEvent);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.currentTarget !== e.target) {
      return;
    }

    if ((e.key === "Enter" || e.key === " ") && onRowClick) {
      e.preventDefault();
      onRowClick(item);
    }
  };

  const showSelection = !!selection && !emptyState;
  const isSelected = !!selection?.isItemSelected(item);
  const itemId =
    typeof item === "object" &&
    item !== null &&
    "id" in item &&
    typeof item.id === "string"
      ? item.id
      : undefined;

  return (
    <div
      data-e2e-id={itemId ? `table-row-${itemId}` : undefined}
      className={cn(
        "grid min-w-fit border-b border-neutral-500/5 hover:bg-neutral-500/5",
        onRowClick && [
          "cursor-pointer",
          "relative focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:z-10",
        ],
        heightClass,
        className,
      )}
      style={{
        gridTemplateColumns,
        outlineOffset: -2,
      }}
      role="row"
      tabIndex={onRowClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={onRowClick ? t("table.clickableRowAriaLabel") : undefined}
    >
      {showSelection && (
        <div
          className="flex items-center justify-center min-w-0"
          onClick={handleCheckboxCellClick}
        >
          <Checkbox
            checked={isSelected}
            onChange={selection.onSelectRow(item)}
            size="sm"
            aria-label={t("table.selectRowAriaLabel")}
          />
        </div>
      )}
      {columns.map((column) => {
        const cellContent = onRenderCell
          ? onRenderCell(column, item)
          : ((item as any)[column.key] ?? "");

        return (
          <TableCell key={column.key} align={column.align}>
            {cellContent}
          </TableCell>
        );
      })}
    </div>
  );
}
